import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send } from "lucide-react";
import { toast } from "sonner";

export const DealComments = ({ deal }) => {
  const { t } = useLang();
  const { can } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/deals/${deal.id}/comments`);
    setComments(res.data);
  }, [deal.id]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/deals/${deal.id}/comments`, { text });
      setText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="border-t border-slate-200 pt-4 mt-2 space-y-3" data-testid="deal-comments-panel">
      <h4 className="text-sm font-semibold text-slate-900">{t("comments")}</h4>

      <div className="max-h-40 overflow-y-auto space-y-2 crm-scroll">
        {comments.length === 0 ? (
          <p className="text-xs text-slate-400">{t("no_comments")}</p>
        ) : comments.map((c) => (
          <div key={c.id} className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2">
            <div className="flex justify-between text-[11px] text-slate-500 mb-0.5">
              <span className="font-semibold text-slate-700">{c.author_name}</span>
              <span>{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <p className="text-slate-700 whitespace-pre-wrap">{c.text}</p>
          </div>
        ))}
      </div>

      {can("comments:write") && (
        <div className="space-y-2">
          <Textarea
            rows={2}
            value={text}
            data-testid="comment-input"
            placeholder={t("add_comment")}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center justify-between gap-2">
            <Button size="sm" onClick={send} data-testid="send-comment-button" className="bg-slate-900 hover:bg-slate-800 gap-1.5">
              <Send className="w-3.5 h-3.5" /> {t("send")}
            </Button>
            {can("deals:approve") && (
              <span className="text-[11px] text-slate-400" data-testid="approval-hint">{t("approve_in_approvals_page")}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealComments;
